import * as React from 'react'
import {ResponsiveImage} from './ResponsiveImage'
import type {ResponsiveImageData} from '../../types/responsiveImage'

export type PersonCardVariant = 'large' | 'medium' | 'small'

export interface PersonCardProps {
    id: string
    name: string
    jobTitle?: string
    bio?: string
    email?: string
    phone?: string
    department?: string
    image?: ResponsiveImageData
    headingLevel?: 'h2' | 'h3'
    variant?: PersonCardVariant
    className?: string
}

interface VariantStyles {
    name: string
    body: string
    hideBodyOnDesktop: boolean
}

const variantStyles: Record<PersonCardVariant, VariantStyles> = {
    large: {
        name: 'text-2xl leading-tight md:text-3xl md:leading-snug',
        body: 'text-base md:text-xl',
        hideBodyOnDesktop: false,
    },
    medium: {
        name: 'text-2xl leading-tight md:text-3xl md:leading-snug',
        body: 'text-base',
        hideBodyOnDesktop: false,
    },
    small: {
        name: 'text-2xl leading-tight',
        body: 'text-base',
        hideBodyOnDesktop: true,
    },
}

export const PersonCard = ({
    name,
    jobTitle,
    bio,
    department,
    image,
    headingLevel = 'h3',
    variant = 'large',
    className = '',
}: PersonCardProps) => {
    const Heading = headingLevel
    const styles = variantStyles[variant]
    const hideOnDesktop = styles.hideBodyOnDesktop ? 'md:hidden' : ''

    return (
        <article className={`flex flex-col bg-white pb-8 ${className}`}>
            {image ? (
                <div className="aspect-4/3 overflow-hidden">
                    <ResponsiveImage
                        data={image}
                        className="h-full w-full object-cover"
                        loading="lazy"
                    />
                </div>
            ) : (
                <div className="aspect-4/3 bg-slate-300" />
            )}

            <div className="flex flex-col gap-3 px-5 pt-5 md:gap-5 md:px-8 md:pt-8">
                <div className="flex flex-col gap-3">
                    {department && (
                        <span className="self-start rounded bg-blue-300 px-4 py-1 text-xs leading-snug text-black md:text-sm">
                            {department}
                        </span>
                    )}

                    <div className="flex flex-col gap-2">
                        <Heading className={`font-bold text-blue-900 ${styles.name}`}>
                            {name}
                        </Heading>
                        {jobTitle && (
                            <p className="text-sm leading-normal text-slate-900 md:text-base">
                                {jobTitle}
                            </p>
                        )}
                    </div>
                </div>

                {bio && (
                    <p className={`leading-normal text-slate-900 ${styles.body} ${hideOnDesktop}`}>
                        {bio}
                    </p>
                )}
            </div>
        </article>
    )
}
